import React, { useState, useEffect, useCallback } from "react";
import Header from "../../../commons/Header/Header";
import Sidebar from "../../../commons/Sidebar/Sidebar";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Img from "../../../assets/images/img.webp";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../../utils/constants";

const getDishImageUrl = (image) => {
  if (!image || typeof image !== "string") return Img;
  if (image.startsWith("http")) return image;
  const base = process.env.REACT_APP_IMAGE_URL || "";
  if (!base) return Img;
  return `${base.replace(/\/$/, "")}/${image.replace(/^\//, "")}`;
};

function ItemsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [menuItem, setMenuItem] = useState(null);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, {
        state: location.state?.listState ? { listState: location.state.listState } : undefined,
      });
      return;
    }
    navigate("/menu-and-restaurant-management");
  };

  const getMenuItemDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.menuCatalogueDetail}/${id}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setMenuItem(response?.data?.data);
      } else {
        toast.error(response?.data?.message || "Failed to fetch menu item details");
      }
    } catch (error) {
      console.error("Error fetching menu item details:", error);
      toast.error("Failed to fetch menu item details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getMenuItemDetails();
  }, [getMenuItemDetails]);

  const dishImageSrc = menuItem ? getDishImageUrl(menuItem.image) : Img;
  const addOns = menuItem?.dish_add_on || [];

  if (loading) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="heading mb-3 d-flex align-items-center">
                <h3 className="text-default weight-400 my-0">Items Details</h3>
              </div>
              <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Items Details</h3>
            </div>
            <div className="mt-3">
              <button type="button" className="btn btn-default rounded-lg" onClick={handleBack}>
                Back
              </button>
            </div>
            {menuItem ? (
              <>
                <div className="menu-item-detail__layout mt-4">
                  <div className="menu-item-detail__image">
                    <img
                      src={dishImageSrc}
                      alt={menuItem.dish_name || "Menu item"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = Img;
                      }}
                    />
                  </div>
                  <div className="menu-item-detail__content">
                    <h5 className="mb-2 d-flex align-items-center">
                      {menuItem.dish_name || "N/A"}
                      <span className={`status_3 ${menuItem.status === 1 ? "online" : "offline"}`} />
                    </h5>
                    {menuItem.restaurant?.restaurant_name && (
                      <p className="text-muted small mb-2">{menuItem.restaurant.restaurant_name}</p>
                    )}
                    <div className="border-bottom small mb-3 pb-3">
                      {menuItem.description || "No description available"}
                    </div>
                    <div className="border border-right-0 border-bottom-0 rounded-14 overflow-hidden">
                      <table className="w-100 text-center mb-0">
                        <tbody>
                          <tr>
                            {menuItem.dish_price_quantity?.length > 0 ? (
                              menuItem.dish_price_quantity.map((priceItem, index) => (
                                <td key={priceItem._id || index} className="px-3 py-3 border-right border-bottom">
                                  <div className="font-weight-600">
                                    {priceItem.variation_detail?.name || "N/A"}
                                  </div>
                                  £{priceItem.price || "N/A"}
                                </td>
                              ))
                            ) : (
                              <td className="px-3 py-3 border-right border-bottom">
                                <div className="font-weight-600">Price</div>
                                N/A
                              </td>
                            )}
                            <td className="px-3 py-3 border-right border-bottom">
                              <div className="font-weight-600">Food Preference</div>
                              {menuItem.food_preference?.name || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-3 py-3 border-right border-bottom">
                              <div className="font-weight-600">Preparation Time</div>
                              {menuItem.preparation_time ? `${menuItem.preparation_time} min` : "N/A"}
                            </td>
                            <td className="px-3 py-3 border-right border-bottom">
                              <div className="font-weight-600">Status</div>
                              {menuItem.status === 1 ? "Active" : "Inactive"}
                            </td>
                            <td className="px-3 py-3 border-right border-bottom" colSpan="2" />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 px-0 mx-0 mt-3">
                  <h4 className="mb-3">INGREDIENTS</h4>
                  {menuItem.dish_ingredient?.length > 0 ? (
                    <ul className="mb-0">
                      {menuItem.dish_ingredient.map((item) => (
                        <li key={item._id}>{item.ingredient?.name || "N/A"}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0">No ingredients available</p>
                  )}
                </div>

                {addOns.length > 0 && (
                  <div className="col-md-12 px-0 mx-0 mt-4">
                    <h4 className="mb-3">ADD-ONS</h4>
                    <div className="col-md-6 col-lg-5 px-0">
                      <div className="table-responsive menu-item-detail__addons-table">
                        <table className="table table-striped trim mb-0">
                          <thead>
                            <tr>
                              <th width="80" className="text-center">
                                S No
                              </th>
                              <th className="text-center">Add-on name</th>
                              <th>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {addOns.map((addon, index) => (
                              <tr key={addon._id || index}>
                                <td className="text-center">{index + 1}</td>
                                <td className="text-center">{addon.dish_name || "N/A"}</td>
                                <td>£{addon.price ?? "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p>No menu item details available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ItemsDetails;
